import { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { mapConfigFromApi } from './bankingApiMappers';
import { buildBankingEntityPrefixes } from './bankingHelpers';

export const DEFAULT_BANQUE_CONFIG = mapConfigFromApi({});

let hydratePromise = null;

function hydrateBanqueConfig(patchSettings) {
  if (!hydratePromise) {
    hydratePromise = localApiAdapter
      .get('/banking/config')
      .then((data) => {
        const normalized = mapConfigFromApi(data || {});
        syncBanqueConfigToSettings(normalized, patchSettings, data || {});
        return normalized;
      })
      .catch(() => {
        hydratePromise = null;
        return DEFAULT_BANQUE_CONFIG;
      });
  }
  return hydratePromise;
}

/** Live banking module config — synced from API once, then from SettingsContext. */
export function useBanqueConfig() {
  const { settings, patchSettings } = useSettings();

  useEffect(() => {
    if (settings?.banking && Object.keys(settings.banking).length > 0) return;
    void hydrateBanqueConfig(patchSettings);
  }, [settings?.banking, patchSettings]);

  return useMemo(() => {
    if (settings?.banking?._ui) {
      return mapConfigFromApi({ _ui: settings.banking._ui, ...(settings.banking._api || {}) });
    }
    return mapConfigFromApi(settings?.banking?._api || settings?.banking || {});
  }, [settings?.banking]);
}

export function syncBanqueConfigToSettings(config, patchSettings, apiPayload = null, existingSettings = {}) {
  const normalized = mapConfigFromApi(apiPayload || { _ui: config });
  patchSettings(
    {
      banking: {
        ...normalized,
        _ui: config,
        _api: apiPayload,
        _syncedFromApi: true,
      },
      configuration: {
        entityPrefixes: {
          ...(existingSettings?.configuration?.entityPrefixes || {}),
          ...buildBankingEntityPrefixes(config),
        },
      },
    },
    { persist: false },
  );
  return normalized;
}

/** Cap list rows to configured itemsPerPage (live from banking config). */
export function sliceBanqueListItems(items = [], itemsPerPage = 50) {
  const limit = Math.max(1, parseInt(itemsPerPage, 10) || 50);
  return items.slice(0, limit);
}

/** Sync table/cards toggle with banking config defaultView (real-time). */
export function useBanqueListViewMode(bankingConfig) {
  const [viewMode, setViewMode] = useState(() => {
    const dv = bankingConfig?.defaultView;
    return dv === 'grid' || dv === 'cards' ? 'cards' : 'table';
  });

  useEffect(() => {
    const dv = bankingConfig?.defaultView;
    setViewMode(dv === 'grid' || dv === 'cards' ? 'cards' : 'table');
  }, [bankingConfig?.defaultView]);

  return [viewMode, setViewMode];
}
